import { useState, useEffect, useCallback } from 'react';
import {
  loadDateTimePreference,
  saveDateTimePreference,
  type DateTimeViewMode,
} from './dateTimePreferences';

// Global state for DateTimeCell view mode - shared across all components
let globalViewMode: DateTimeViewMode = 'relative';
const listeners = new Set<(mode: DateTimeViewMode) => void>();

// Initialize from localStorage
const initViewMode = () => {
  globalViewMode = loadDateTimePreference();
};

// Notify all listeners of view mode change
const notifyListeners = (mode: DateTimeViewMode) => {
  listeners.forEach(listener => listener(mode));
};

// Initialize on module load
initViewMode();

/**
 * Hook for managing DateTimeCell view mode with global synchronization
 * All DateTimeCell components share the same view mode preference
 */
export const useDateTimeViewMode = () => {
  const [viewMode, setViewModeState] = useState<DateTimeViewMode>(() => {
    return globalViewMode;
  });

  // Subscribe to global state changes
  useEffect(() => {
    // Set initial state
    setViewModeState(globalViewMode);

    // Subscribe to changes
    const listener = (mode: DateTimeViewMode) => {
      setViewModeState(mode);
    };

    listeners.add(listener);

    // Listen for storage changes from other tabs/windows
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'datetime-cell-view-preference' && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          globalViewMode = parsed.viewMode;
          notifyListeners(parsed.viewMode);
        } catch (error) {
          console.error('Failed to parse storage change:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      listeners.delete(listener);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Update view mode function
  const setViewMode = useCallback((newMode: DateTimeViewMode) => {
    globalViewMode = newMode;
    saveDateTimePreference(newMode);
    notifyListeners(newMode);
  }, []);

  return {
    viewMode,
    setViewMode,
  };
};

